import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, CardTitle, Alert, Input, FormGroup, Label } from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';

const Reporting = () => {
  const firebase = useContext(FirebaseContext);
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);
  const currentOrgName = useOrganizationStore(state => state.currentOrgName);
  const currentOrgRole = useOrganizationStore(state => state.currentOrgRole);
  const isOrgAdmin = currentOrgRole === 'ADMIN';

  const [connectionStatus, setConnectionStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [configStatus, setConfigStatus] = useState(null);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [newSheetUrl, setNewSheetUrl] = useState('');
  const [loading, setLoading] = useState({
    connection: false,
    sync: false,
    config: false,
    update: false
  });

  const handleConnectionTest = async () => {
    try {
      setLoading(prev => ({ ...prev, connection: true }));
      setConnectionStatus(null);

      const result = await firebase.callFunction('testGoogleSheetsConnection', { orgId: currentOrgId });
      
      // Handle both direct result and wrapped result
      const data = result.data || result;
      
      if (data.success) {
        setConnectionStatus({
          type: 'success',
          message: `✅ ${data.message}`,
          details: `Sheet: "${data.sheetTitle}" (ID: ${data.sheetId})`
        });
      } else {
        setConnectionStatus({
          type: 'danger',
          message: '❌ Connection test failed',
          details: data.message || 'Unknown error'
        });
      }
    } catch (error) {
      setConnectionStatus({
        type: 'danger',
        message: '❌ Connection test failed',
        details: error.message || 'Network error'
      });
    } finally {
      setLoading(prev => ({ ...prev, connection: false }));
    }
  };

  const handleManualSync = async () => {
    try {
      setLoading(prev => ({ ...prev, sync: true }));
      setSyncStatus(null);
      
      const result = await firebase.callFunction('manualSyncToSheets', { orgId: currentOrgId });
      
      // Handle both direct result and wrapped result
      const data = result.data || result;
      
      if (data.success) {
        setSyncStatus({
          type: 'success',
          message: `✅ ${data.message}`,
          details: `Synced ${data.tracksCount} tracks at ${new Date(data.timestamp).toLocaleString()}`
        });
      } else {
        setSyncStatus({
          type: 'danger',
          message: '❌ Manual sync failed',
          details: data.message || 'Unknown error'
        });
      }
    } catch (error) {
      setSyncStatus({
        type: 'danger',
        message: '❌ Manual sync failed',
        details: error.message || 'Network error'
      });
    } finally {
      setLoading(prev => ({ ...prev, sync: false }));
    }
  };

  const loadCurrentConfig = useCallback(async () => {
    if (!currentOrgId || !isOrgAdmin) return;
    try {
      setLoading(prev => ({ ...prev, config: true }));

      const result = await firebase.callFunction('getReportingConfiguration', { orgId: currentOrgId });
      const data = result.data || result;

      if (data.success) {
        setCurrentConfig(data.config);
        setNewSheetUrl(data.config.sheetUrl || '');
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(prev => ({ ...prev, config: false }));
    }
  }, [firebase, currentOrgId, isOrgAdmin]);

  // Load configuration on mount and whenever the org changes.
  useEffect(() => {
    // Clear stale state from the previous org before reloading.
    setCurrentConfig(null);
    setNewSheetUrl('');
    setConnectionStatus(null);
    setSyncStatus(null);
    setConfigStatus(null);
    loadCurrentConfig();
  }, [loadCurrentConfig]);

  const handleUpdateSheetUrl = async () => {
    try {
      setLoading(prev => ({ ...prev, update: true }));
      setConfigStatus(null);
      
      if (!newSheetUrl.trim()) {
        setConfigStatus({
          type: 'danger',
          message: '❌ Sheet URL is required',
          details: 'Please enter a valid Google Sheets URL'
        });
        return;
      }
      
      const result = await firebase.callFunction('updateReportingConfiguration', {
        orgId: currentOrgId,
        sheetUrl: newSheetUrl.trim()
      });
      
      const data = result.data || result;
      
      if (data.success) {
        setConfigStatus({
          type: 'success',
          message: `✅ ${data.message}`,
          details: `Updated for year ${data.config.year} by ${data.config.updatedBy}`
        });
        
        // Reload configuration
        await loadCurrentConfig();
      } else {
        setConfigStatus({
          type: 'danger',
          message: '❌ Failed to update configuration',
          details: data.message || 'Unknown error'
        });
      }
    } catch (error) {
      setConfigStatus({
        type: 'danger',
        message: '❌ Failed to update configuration',
        details: error.message || 'Network error'
      });
    } finally {
      setLoading(prev => ({ ...prev, update: false }));
    }
  };

  if (!currentOrgId) {
    return (
      <div className="pt-3">
        <Container>
          <Alert color="warning">Select an organization to configure reporting.</Alert>
        </Container>
      </div>
    );
  }

  if (!isOrgAdmin) {
    return (
      <div className="pt-3">
        <Container>
          <Alert color="warning">
            You need <strong>ADMIN</strong> role in <strong>{currentOrgName}</strong> to view reporting.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="pt-3">
      <Container>
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <CardTitle tag="h4">Google Sheets Reporting</CardTitle>
                <p className="text-muted">
                  Manage Google Sheets integration for <strong className="text-white">{currentOrgName}</strong>.
                  Each organization has its own sheet — switching organizations shows a different configuration.
                </p>
                
                <Row className="mt-4">
                  <Col md="6">
                    <Card className="card-stats">
                      <CardBody>
                        <div className="d-flex justify-content-between">
                          <div>
                            <p className="card-category">Connection Status</p>
                            <h5 className="card-title">Google Sheets API</h5>
                          </div>
                          <div className="icon-big text-center">
                            <i className="nc-icon nc-globe text-info"></i>
                          </div>
                        </div>
                        <div className="mt-3">
                          <button
                            className="btn btn-info-custom btn-round btn-sm"
                            onClick={handleConnectionTest}
                            disabled={loading.connection}
                          >
                            {loading.connection ? (
                              <span style={{ position: 'relative', zIndex: 3 }}>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Testing...
                              </span>
                            ) : (
                              <span style={{ position: 'relative', zIndex: 3 }}>Test Connection</span>
                            )}
                          </button>
                        </div>
                        {connectionStatus && (
                          <Alert color={connectionStatus.type} className="mt-3">
                            <div>{connectionStatus.message}</div>
                            <small>{connectionStatus.details}</small>
                          </Alert>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                  
                  <Col md="6">
                    <Card className="card-stats">
                      <CardBody>
                        <div className="d-flex justify-content-between">
                          <div>
                            <p className="card-category">Data Sync</p>
                            <h5 className="card-title">Manual Sync</h5>
                          </div>
                          <div className="icon-big text-center">
                            <i className="nc-icon nc-refresh-69 text-success"></i>
                          </div>
                        </div>
                        <div className="mt-3">
                          <button
                            className="btn btn-success-custom btn-round btn-sm"
                            onClick={handleManualSync}
                            disabled={loading.sync}
                          >
                            {loading.sync ? (
                              <span style={{ position: 'relative', zIndex: 3 }}>
                                <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                Syncing...
                              </span>
                            ) : (
                              <span style={{ position: 'relative', zIndex: 3 }}>Sync Now</span>
                            )}
                          </button>
                        </div>
                        {syncStatus && (
                          <Alert color={syncStatus.type} className="mt-3">
                            <div>{syncStatus.message}</div>
                            <small>{syncStatus.details}</small>
                          </Alert>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md="12">
                    <Card>
                      <CardBody>
                        <CardTitle tag="h5">⚙️ Sheet Configuration</CardTitle>
                        <p className="text-muted">
                          Update the Google Sheets URL for yearly reporting. Changes take effect immediately without redeployment.
                        </p>
                        
                        {loading.config ? (
                          <div className="text-center py-3">
                            <span className="spinner-border" role="status" aria-hidden="true"></span>
                            <p className="text-muted mt-2">Loading current configuration...</p>
                          </div>
                        ) : (
                          <>
                            {currentConfig && (
                              <div className="mb-4 p-3" style={{ background: 'rgba(255, 255, 255, 0.05)', borderRadius: 8 }}>
                                <h6>Current Configuration</h6>
                                {currentConfig.sheetUrl ? (
                                  <>
                                    <p><strong>Sheet URL:</strong> <small className="text-muted">{currentConfig.sheetUrl}</small></p>
                                    <p><strong>Year:</strong> {currentConfig.year}</p>
                                    {currentConfig.updatedBy && (
                                      <p><strong>Last Updated:</strong> by {currentConfig.updatedBy}</p>
                                    )}
                                  </>
                                ) : (
                                  <p className="text-muted mb-0">No sheet configured yet for {currentOrgName}. Paste a Google Sheets URL below to get started.</p>
                                )}
                              </div>
                            )}
                            
                            <FormGroup>
                              <Label for="sheetUrl">New Google Sheets URL</Label>
                              <Input
                                type="url"
                                id="sheetUrl"
                                placeholder="https://docs.google.com/spreadsheets/d/[ID]/edit..."
                                value={newSheetUrl}
                                onChange={(e) => setNewSheetUrl(e.target.value)}
                                disabled={loading.update}
                              />
                              <small className="text-muted">
                                Paste the full URL of your Google Sheet. Make sure to share the sheet with the service account.
                              </small>
                            </FormGroup>
                            
                            <button
                              className="btn btn-warning-custom btn-round btn-sm"
                              onClick={handleUpdateSheetUrl}
                              disabled={loading.update || !newSheetUrl.trim()}
                            >
                              {loading.update ? (
                                <span style={{ position: 'relative', zIndex: 3 }}>
                                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                                  Updating...
                                </span>
                              ) : (
                                <span style={{ position: 'relative', zIndex: 3 }}>Update Sheet URL</span>
                              )}
                            </button>
                            
                            {configStatus && (
                              <Alert color={configStatus.type} className="mt-3">
                                <div>{configStatus.message}</div>
                                <small>{configStatus.details}</small>
                              </Alert>
                            )}
                          </>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md="12">
                    <Card>
                      <CardBody>
                        <CardTitle tag="h5">📋 Sync Information</CardTitle>
                        <div className="info-section">
                          <p><strong>Data Source:</strong> Firestore audiometa collection</p>
                          <p><strong>Date Filter:</strong> Current year tracks only (dateCompleted >= Jan 1, {new Date().getFullYear()})</p>
                          <p><strong>Fields Synced:</strong> agency, bpm, client, composer, dateCompleted, genre, key, trackName</p>
                          <p><strong>Sync Method:</strong> Complete sheet overwrite (ensures data consistency)</p>
                          <p><strong>Automatic Sync:</strong> Triggered on any audiometa collection change</p>
                          <p><strong>Environment:</strong> Production only (music-vault-22faa)</p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md="12">
                    <Card>
                      <CardBody>
                        <CardTitle tag="h5">⚠️ Important Notes</CardTitle>
                        <ul className="list-unstyled text-muted">
                          <li>• Google Sheets sync is only available in <strong>production environment</strong></li>
                          <li>• Sheet URL can be updated instantly without redeployment</li>
                          <li>• Manual sync will overwrite all existing data in the sheet</li>
                          <li>• Only tracks with completion date in current year are synced (automatic year rollover)</li>
                          <li>• Automatic sync triggers on any track add/edit/delete</li>
                          <li>• Service account requires Editor permissions on the target sheet</li>
                          <li>• Configuration is stored in Firestore for runtime updates</li>
                        </ul>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Reporting;